import { Alert, Box, Collapse, Stack } from "@mui/material";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { useBrandTones } from "../../modules/brand-kit/hooks/use-brand-tones";
import { useFontSelections } from "../../modules/brand-kit/hooks/use-font-selections";
import { BrandingBar } from "../../modules/branding-bar/branding-bar";
import { ContentPreview } from "../../modules/content-previews/content-preview";
import { ContentPreviews } from "../../modules/content-previews/content-previews";
import { CreateContentCard } from "../../modules/create-content-card/create-content-card";
import { CreateContentCardNew } from "../../modules/create-content-card/create-content-card-new";
import { PromptSummary } from "../../modules/create-content-card/prompt-summary";
import { PromptSummaryNew } from "../../modules/create-content-card/prompt-summary-new";
import { useAccount } from "../../modules/generic/hooks/use-account";
import { Page } from "../../modules/layout/components/page";
import { useMultiformat } from "../../modules/multiformat/hooks/use-multi-format";
import type { MultiformatDimensions } from "../../modules/multiformat/store/types";
import { useFlag } from "../../server/optimizely";
import { useCustomTemplates } from "./use-custom-templates";

const CreateContentPreviewPage: FC = () => {
  const { t } = useTranslation();
  const [new_page] = useFlag("create_content_page_v2");

  const { data: multiformat, error: multiformatError } = useMultiformat();
  const { data: account, error: accountError } = useAccount();
  const { data: fontSelections, loading: fontSelectionsLoading } = useFontSelections();
  const { data: customTemplates, loading: customTemplatesLoading } =
    useCustomTemplates(multiformat);
  const { data: brandTones, loading: brandTonesLoading } = useBrandTones();
  const [searchParams] = useSearchParams();
  const [showForm, setShowForm] = useState(false);
  const [useSelectedLogo, setUseSelectedLogo] = useState(false);

  const contentTypeName = useMemo(() => searchParams.get("format"), [searchParams]);
  const answer = useMemo(() => searchParams.get("answer"), [searchParams]);
  const db = useMemo(() => searchParams.get("db"), [searchParams]);
  const fileId = useMemo(() => searchParams.get("file_id"), [searchParams]);
  const imageStyles = useMemo(() => searchParams.get("image_styles"), [searchParams])?.split(",");
  const tone = useMemo(() => searchParams.get("tone"), [searchParams]);
  const handleReset = useCallback(() => {
    setUseSelectedLogo(false);
  }, []);

  const handleLogoChange = useCallback(() => {
    setUseSelectedLogo(true);
  }, []);

  const multiformatConfig = multiformat?.content_types.find((c) => c.name === contentTypeName);
  return (
    <Page title={t("Create Content Preview | ChangeEngine")} sx={{ maxWidth: "xl" }}>
      <Box sx={{ position: "relative", minHeight: "2em" }}>
        {new_page ? (
          <PromptSummaryNew
            onClick={() => setShowForm(true)}
            sx={{ position: "absolute", width: "100%" }}
          />
        ) : (
          <PromptSummary
            onClick={() => setShowForm(true)}
            sx={{ position: "absolute", width: "100%" }}
          />
        )}
        <Collapse in={showForm} sx={{ position: "relative" }}>
          {new_page ? (
            <CreateContentCardNew onClose={() => setShowForm(false)} />
          ) : (
            <CreateContentCard onClose={() => setShowForm(false)} />
          )}
        </Collapse>
      </Box>
      <Stack gap={4} sx={(theme) => ({ [theme.breakpoints.down("md")]: { m: 3 }, m: 5 })}>
        <Stack direction="row-reverse">
          <BrandingBar
            compact={true}
            useSelectedLogo={useSelectedLogo}
            onLogoChange={handleLogoChange}
            onReset={handleReset}
          />
        </Stack>
        {multiformatConfig?.design_huddle?.dimensions &&
        contentTypeName &&
        account &&
        !customTemplatesLoading &&
        !fontSelectionsLoading &&
        !brandTonesLoading ? (
          <ContentPreviews
            contentTypeName={contentTypeName}
            answer={answer ?? ""}
            useSelectedLogo={useSelectedLogo}
            dimensions={multiformatConfig.design_huddle?.dimensions}
            customTemplates={customTemplates}
            account={account}
            fontSelections={fontSelections}
            db={db}
            fileId={fileId ?? undefined}
            imageStyles={imageStyles}
            brandTones={brandTones}
            tone={tone ?? undefined}
          />
        ) : (
          multiformatConfig?.design_huddle?.dimensions && (
            <PreviewsSkeletons dimensions={multiformatConfig.design_huddle.dimensions} />
          )
        )}
        {(multiformatError || accountError) && <Error />}
      </Stack>
    </Page>
  );
};

export default CreateContentPreviewPage;

const PreviewsSkeletons: FC<{ dimensions: MultiformatDimensions }> = ({ dimensions }) => {
  return (
    <Box
      sx={[
        (theme) => ({
          display: "grid",
          gap: 2,
          [theme.breakpoints.down("xs")]: {
            gridTemplateColumns: "1fr",
          },
          [theme.breakpoints.up("sm")]: {
            gridTemplateColumns: "repeat(2, 1fr)",
          },
          [theme.breakpoints.up("lg")]: {
            gridTemplateColumns: "repeat(2, 1fr)",
          },
        }),
      ]}
    >
      {Array.from({ length: 4 }).map((_, i) => (
        <ContentPreview
          key={i}
          image={{ state: "loading" }}
          hidden={false}
          dimensions={dimensions}
        />
      ))}
    </Box>
  );
};

const Error: FC = () => {
  const { t } = useTranslation();
  return (
    <Alert severity="error">
      {t("Sorry, we encountered a problem trying to load this page. Please try again.")}
    </Alert>
  );
};
